import { computed, type ComputedRef } from 'vue';
import { useAccountStore } from '@/stores/account';
import { useRemoteConfigStore } from '@/shared/firebase';
import { OFFSHORE_ELIGIBLE_COUNTRIES } from '../constants/card.constant';

/**
 * Regla única de elegibilidad para el flujo "Virtual en dólares" (BMB-3780).
 *
 * Un usuario es elegible cuando su país está en `OFFSHORE_ELIGIBLE_COUNTRIES` y
 * `feature_offshore_smartcard_chl` está habilitado en Remote Config para él.
 *
 * Se toma el país desde `accountStore.getCompanyCountry` (fuente histórica del
 * módulo, la misma que usa `useCardAvailability`) para evitar dos fuentes de
 * verdad y no depender del orden de carga de wallets en `verifyWalletAndCardTerms`.
 *
 * @returns `countryCode` normalizado en mayúscula y `isCHLOffshoreEligible` reactivo.
 */
export function useOffshoreEligibility(): {
  countryCode: ComputedRef<string>;
  isCHLOffshoreEligible: ComputedRef<boolean>;
} {
  const accountStore = useAccountStore();
  const remoteConfigStore = useRemoteConfigStore();
  const offshoreSmartCardChlConfig = remoteConfigStore.getFeatureFlag(
    'feature_offshore_smartcard_chl'
  );

  const countryCode = computed(() =>
    (accountStore.getCompanyCountry || '').toUpperCase()
  );

  const isCHLOffshoreEligible = computed<boolean>(
    () =>
      (OFFSHORE_ELIGIBLE_COUNTRIES as readonly string[]).includes(
        countryCode.value
      ) && Boolean(offshoreSmartCardChlConfig.value.enabled)
  );

  return { countryCode, isCHLOffshoreEligible };
}
